import { forwardRef } from "react";
import type { ComponentProps, SVGProps } from "react";
import {
  Activity01Icon,
  Alert02Icon,
  AlertCircleIcon,
  ArrowDown01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  ArrowUp01Icon,
  ArrowUpRight01Icon,
  Attachment01Icon,
  BookOpen01Icon,
  Bookmark01Icon,
  Cancel01Icon,
  CheckmarkCircle02Icon,
  Clock03Icon,
  CodeIcon,
  ComputerIcon,
  Copy01Icon,
  CreditCardIcon,
  CrownIcon,
  DashboardSquare01Icon,
  Delete02Icon,
  Download01Icon,
  EyeIcon,
  Globe02Icon,
  InformationCircleIcon,
  Key01Icon,
  Loading03Icon,
  LockIcon,
  Logout01Icon,
  Mail01Icon,
  Menu01Icon,
  Message01Icon,
  MinusSignIcon,
  Moon01Icon,
  Moon02Icon,
  MoreHorizontalCircle01Icon,
  Notification03Icon,
  PencilEdit02Icon,
  PlayIcon,
  PlusSignIcon,
  Refresh01Icon,
  Rocket01Icon,
  Search01Icon,
  SentIcon,
  ServerStack01Icon,
  Settings01Icon,
  Shield01Icon,
  ShieldKeyIcon,
  SmartPhone01Icon,
  SparklesIcon,
  Sun01Icon,
  TestTube01Icon,
  Tick02Icon,
  UserAdd01Icon,
  UserGroup02Icon,
  ViewOffSlashIcon,
  WebhookIcon,
  ZapIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon as BaseHugeiconsIcon } from "@hugeicons/react";
import {
  Activity as LucideActivity,
  AlertCircle as LucideAlertCircle,
  AlertTriangle as LucideAlertTriangle,
  ArrowLeft as LucideArrowLeft,
  ArrowRight as LucideArrowRight,
  Bell as LucideBell,
  BookOpen as LucideBookOpen,
  Bookmark as LucideBookmark,
  Check as LucideCheck,
  CheckCircle2 as LucideCheckCircle2,
  ChevronDown as LucideChevronDown,
  ChevronLeft as LucideChevronLeft,
  ChevronRight as LucideChevronRight,
  ChevronUp as LucideChevronUp,
  Clock as LucideClock,
  Code2 as LucideCode2,
  Copy as LucideCopy,
  CreditCard as LucideCreditCard,
  Crown as LucideCrown,
  Download as LucideDownload,
  ExternalLink as LucideExternalLink,
  Eye as LucideEye,
  EyeOff as LucideEyeOff,
  Gauge as LucideGauge,
  Globe as LucideGlobe,
  InfoIcon as LucideInfoIcon,
  Key as LucideKey,
  Loader2 as LucideLoader2,
  Lock as LucideLock,
  LogOut as LucideLogOut,
  Mail as LucideMail,
  Menu as LucideMenu,
  MessageSquare as LucideMessageSquare,
  MinusIcon as LucideMinusIcon,
  MoreHorizontal as LucideMoreHorizontal,
  Monitor as LucideMonitor,
  Moon as LucideMoon,
  Paperclip as LucidePaperclip,
  Pencil as LucidePencil,
  Play as LucidePlay,
  Plus as LucidePlus,
  RefreshCw as LucideRefreshCw,
  Rocket as LucideRocket,
  Search as LucideSearch,
  Send as LucideSend,
  Server as LucideServer,
  Settings as LucideSettings,
  ShieldAlert as LucideShieldAlert,
  ShieldCheck as LucideShieldCheck,
  ShieldX as LucideShieldX,
  Smartphone as LucideSmartphone,
  Sparkles as LucideSparkles,
  Sun as LucideSun,
  TestTube as LucideTestTube,
  Trash2 as LucideTrash2,
  UserPlus as LucideUserPlus,
  Users as LucideUsers,
  Webhook as LucideWebhook,
  X as LucideX,
  Zap as LucideZap,
} from "lucide-react";

export type IconPack = "huge" | "lucide";

const normalizeIconPack = (value: string | undefined): IconPack =>
  value?.toLowerCase() === "lucide" ? "lucide" : "huge";

export const ACTIVE_ICON_PACK = normalizeIconPack(import.meta.env.VITE_ICON_PACK);

type HugeIcon = ComponentProps<typeof BaseHugeiconsIcon>["icon"];
type IconProps = SVGProps<SVGSVGElement> & {
  size?: string | number;
  strokeWidth?: string | number;
  absoluteStrokeWidth?: boolean;
};

type LucideIcon = typeof LucideActivity;

type IconEntry = {
  huge: HugeIcon;
  lucide: LucideIcon;
};

const hugeToLucide = new Map<HugeIcon, LucideIcon>();

function defineIcon(huge: HugeIcon, lucide: LucideIcon): IconEntry {
  hugeToLucide.set(huge, lucide);
  return { huge, lucide };
}

function renderHuge(entry: IconEntry, props: IconProps, ref: React.Ref<SVGSVGElement>) {
  const { strokeWidth, ...rest } = props;
  const parsedStrokeWidth =
    typeof strokeWidth === "string" ? Number.parseFloat(strokeWidth) : strokeWidth;

  return (
    <BaseHugeiconsIcon
      ref={ref}
      icon={entry.huge}
      strokeWidth={Number.isFinite(parsedStrokeWidth as number) ? parsedStrokeWidth : undefined}
      {...(rest as Omit<IconProps, "strokeWidth">)}
    />
  );
}

function renderLucide(entry: IconEntry, props: IconProps, ref: React.Ref<SVGSVGElement>) {
  const IconComponent = entry.lucide;
  return <IconComponent ref={ref} {...props} />;
}

function makeIcon(entry: IconEntry) {
  return forwardRef<SVGSVGElement, IconProps>((props, ref) =>
    ACTIVE_ICON_PACK === "lucide" ? renderLucide(entry, props, ref) : renderHuge(entry, props, ref)
  );
}

export const HugeiconsIcon = forwardRef<SVGSVGElement, IconProps & { icon: HugeIcon }>(
  ({ icon, ...props }, ref) => {
    if (ACTIVE_ICON_PACK === "lucide") {
      const lucideIcon = hugeToLucide.get(icon);
      if (lucideIcon) {
        return renderLucide({ huge: icon, lucide: lucideIcon }, props, ref);
      }
    }

    const { strokeWidth, ...rest } = props;
    const parsedStrokeWidth =
      typeof strokeWidth === "string" ? Number.parseFloat(strokeWidth) : strokeWidth;

    return (
      <BaseHugeiconsIcon
        ref={ref}
        icon={icon}
        strokeWidth={Number.isFinite(parsedStrokeWidth as number) ? parsedStrokeWidth : undefined}
        {...(rest as Omit<IconProps, "strokeWidth">)}
      />
    );
  }
);

export const Activity = makeIcon(defineIcon(Activity01Icon, LucideActivity));
export const AlertCircle = makeIcon(defineIcon(AlertCircleIcon, LucideAlertCircle));
export const AlertTriangle = makeIcon(defineIcon(Alert02Icon, LucideAlertTriangle));
export const ArrowLeft = makeIcon(defineIcon(ArrowLeft01Icon, LucideArrowLeft));
export const ArrowRight = makeIcon(defineIcon(ArrowRight01Icon, LucideArrowRight));
export const ArrowRightIcon = makeIcon(defineIcon(ArrowRight01Icon, LucideArrowRight));
export const Bell = makeIcon(defineIcon(Notification03Icon, LucideBell));
export const BookOpen = makeIcon(defineIcon(BookOpen01Icon, LucideBookOpen));
export const BookmarkIcon = makeIcon(defineIcon(Bookmark01Icon, LucideBookmark));
export const Check = makeIcon(defineIcon(Tick02Icon, LucideCheck));
export const CheckCircle2 = makeIcon(defineIcon(CheckmarkCircle02Icon, LucideCheckCircle2));
export const CheckIcon = makeIcon(defineIcon(Tick02Icon, LucideCheck));
export const ChevronDown = makeIcon(defineIcon(ArrowDown01Icon, LucideChevronDown));
export const ChevronDownIcon = makeIcon(defineIcon(ArrowDown01Icon, LucideChevronDown));
export const ChevronLeftIcon = makeIcon(defineIcon(ArrowLeft01Icon, LucideChevronLeft));
export const ChevronRight = makeIcon(defineIcon(ArrowRight01Icon, LucideChevronRight));
export const ChevronRightIcon = makeIcon(defineIcon(ArrowRight01Icon, LucideChevronRight));
export const ChevronUp = makeIcon(defineIcon(ArrowUp01Icon, LucideChevronUp));
export const ChevronUpIcon = makeIcon(defineIcon(ArrowUp01Icon, LucideChevronUp));
export const Clock = makeIcon(defineIcon(Clock03Icon, LucideClock));
export const Code2 = makeIcon(defineIcon(CodeIcon, LucideCode2));
export const Copy = makeIcon(defineIcon(Copy01Icon, LucideCopy));
export const CreditCard = makeIcon(defineIcon(CreditCardIcon, LucideCreditCard));
export const Crown = makeIcon(defineIcon(CrownIcon, LucideCrown));
export const Download = makeIcon(defineIcon(Download01Icon, LucideDownload));
export const ExternalLink = makeIcon(defineIcon(ArrowUpRight01Icon, LucideExternalLink));
export const Eye = makeIcon(defineIcon(EyeIcon, LucideEye));
export const EyeOff = makeIcon(defineIcon(ViewOffSlashIcon, LucideEyeOff));
export const Gauge = makeIcon(defineIcon(DashboardSquare01Icon, LucideGauge));
export const Globe = makeIcon(defineIcon(Globe02Icon, LucideGlobe));
export const InfoIcon = makeIcon(defineIcon(InformationCircleIcon, LucideInfoIcon));
export const Key = makeIcon(defineIcon(Key01Icon, LucideKey));
export const Loader2 = makeIcon(defineIcon(Loading03Icon, LucideLoader2));
export const Loader2Icon = makeIcon(defineIcon(Loading03Icon, LucideLoader2));
export const Lock = makeIcon(defineIcon(LockIcon, LucideLock));
export const LogOut = makeIcon(defineIcon(Logout01Icon, LucideLogOut));
export const Mail = makeIcon(defineIcon(Mail01Icon, LucideMail));
export const Menu = makeIcon(defineIcon(Menu01Icon, LucideMenu));
export const MessageSquare = makeIcon(defineIcon(Message01Icon, LucideMessageSquare));
export const MinusIcon = makeIcon(defineIcon(MinusSignIcon, LucideMinusIcon));
export const Monitor = makeIcon(defineIcon(ComputerIcon, LucideMonitor));
export const Moon = makeIcon(defineIcon(Moon02Icon, LucideMoon));
export const MoreHorizontal = makeIcon(defineIcon(MoreHorizontalCircle01Icon, LucideMoreHorizontal));
export const MoreHorizontalIcon = makeIcon(defineIcon(MoreHorizontalCircle01Icon, LucideMoreHorizontal));
export const PanelLeftIcon = makeIcon(defineIcon(Menu01Icon, LucideMenu));
export const Paperclip = makeIcon(defineIcon(Attachment01Icon, LucidePaperclip));
export const Pencil = makeIcon(defineIcon(PencilEdit02Icon, LucidePencil));
export const Play = makeIcon(defineIcon(PlayIcon, LucidePlay));
export const Plus = makeIcon(defineIcon(PlusSignIcon, LucidePlus));
export const RefreshCw = makeIcon(defineIcon(Refresh01Icon, LucideRefreshCw));
export const Rocket = makeIcon(defineIcon(Rocket01Icon, LucideRocket));
export const Search = makeIcon(defineIcon(Search01Icon, LucideSearch));
export const SearchIcon = makeIcon(defineIcon(Search01Icon, LucideSearch));
export const Send = makeIcon(defineIcon(SentIcon, LucideSend));
export const Server = makeIcon(defineIcon(ServerStack01Icon, LucideServer));
export const Settings = makeIcon(defineIcon(Settings01Icon, LucideSettings));
export const ShieldAlert = makeIcon(defineIcon(ShieldKeyIcon, LucideShieldAlert));
export const ShieldCheck = makeIcon(defineIcon(Shield01Icon, LucideShieldCheck));
export const ShieldX = makeIcon(defineIcon(ShieldKeyIcon, LucideShieldX));
export const Smartphone = makeIcon(defineIcon(SmartPhone01Icon, LucideSmartphone));
export const Sparkles = makeIcon(defineIcon(SparklesIcon, LucideSparkles));
export const Sun = makeIcon(defineIcon(Sun01Icon, LucideSun));
export const TestTube = makeIcon(defineIcon(TestTube01Icon, LucideTestTube));
export const Trash2 = makeIcon(defineIcon(Delete02Icon, LucideTrash2));
export const TrendingUp = makeIcon(defineIcon(ArrowUpRight01Icon, LucideArrowRight));
export const TriangleAlertIcon = makeIcon(defineIcon(Alert02Icon, LucideAlertTriangle));
export const UserPlus = makeIcon(defineIcon(UserAdd01Icon, LucideUserPlus));
export const Users = makeIcon(defineIcon(UserGroup02Icon, LucideUsers));
export const Webhook = makeIcon(defineIcon(WebhookIcon, LucideWebhook));
export const X = makeIcon(defineIcon(Cancel01Icon, LucideX));
export const XIcon = makeIcon(defineIcon(Cancel01Icon, LucideX));
export const Zap = makeIcon(defineIcon(ZapIcon, LucideZap));
